import { IContextOptionNormalized } from "../context-options/interfaces/context-options-normalized"
import { AppContextState, AppMainContextType, AppSubContextType } from "../types/app-context"
import { ContextOption, isUserContextType } from "./context-option"

export class ContextManager {
  private _mainContextOptions: IContextOptionNormalized[]
  private _subContextOptions: IContextOptionNormalized[]

  constructor(private _contextOptions: IContextOptionNormalized[]) {
    this._mainContextOptions = this._contextOptions.filter(ContextOption.isMainContextOption)
    this._subContextOptions = this._contextOptions.filter(ContextOption.isSubContextOption)
  }

  getUserContextOption(): IContextOptionNormalized {
    const foundContextOption = this._mainContextOptions.find(ContextOption.isUserContextOption)
    if (!foundContextOption) throw new Error("User context option not found")
    return foundContextOption
  }

  getOwnMemberOption(mainContextId: string): IContextOptionNormalized | undefined {
    const userContextOption = this.getUserContextOption()
    return this._subContextOptions.find((option) => {
      return option.orgId === mainContextId && option.type === "membership" && option.userId === userContextOption.id
    })
  }

  getMainContextList(): AppMainContextType[] {
    return this._mainContextOptions.map(ContextOption.toMainContext).sort((a, b) => {
      if (isUserContextType(a.type)) return 1
      if (isUserContextType(b.type)) return -1
      return a.name.localeCompare(b.name)
    })
  }

  getSubContextList(mainContextId?: string): AppSubContextType[] {
    return this._subContextOptions
      .filter((option) => option.orgId === mainContextId || !mainContextId)
      .map(ContextOption.toSubContext)
  }

  getDefaultMainContext(): AppMainContextType {
    const userContextOption = this.getUserContextOption()
    const orgContextOptions = this._mainContextOptions.filter(ContextOption.isOrganisationContextOption)

    return orgContextOptions.length === 1
      ? ContextOption.toMainContext(orgContextOptions[0])
      : ContextOption.toMainContext(userContextOption)
  }

  getOwnMemberContext(mainContextId: string): AppSubContextType | null {
    const ownMemberOption = this.getOwnMemberOption(mainContextId)
    return ownMemberOption ? ContextOption.toSubContext(ownMemberOption) : null
  }

  getMainContextById(id?: string): AppMainContextType | null {
    const contextOption = this._mainContextOptions.find((option) => option.id === id)
    if (!contextOption) return null
    return ContextOption.toMainContext(contextOption)
  }

  getSubContextById(id?: string): AppSubContextType | null {
    const contextOption = this._subContextOptions.find((option) => option.id === id)
    if (!contextOption) return null
    return ContextOption.toSubContext(contextOption)
  }

  deriveContextFromQuery(query: { mainContext?: string; subContext?: string }): AppContextState {
    const mainContextList = this.getMainContextList()
    const mainContext = this.getMainContextById(query.mainContext)
    const subContext = this.getSubContextById(query.subContext)
    const subContextList = this.getSubContextList(mainContext?.id)

    return {
      mainContext,
      subContext,
      mainContextList,
      subContextList,
    }
  }
}
